/**
 * Custom Select Component
 *
 * Multi-select dropdown with checkboxes.
 * Desktop: dropdown positioned below trigger
 * Mobile: bottom drawer with overlay
 *
 * @package oska
 */

/**
 * CustomSelect class handles all interactions for a single custom select element
 */
class CustomSelect {
  /**
   * @param {HTMLElement} element - The root .js-custom-select element
   */
  constructor(element) {
    this.root = element;
    this.drawer = element.closest(".js-scrolling-drawer");
    this.trigger = element.querySelector(".js-select-trigger");
    this.dropdown = element.querySelector(".js-select-dropdown");
    this.overlay = element.querySelector(".js-select-overlay");
    this.options = element.querySelectorAll(".js-select-option");
    this.clearBtn = element.querySelector(".js-select-clear");
    this.closeBtn = element.querySelector(".js-select-close");
    this.badge = element.querySelector(".js-select-badge");

    this.isOpen = false;
    this.selectedValues = new Set();

    // Store instance reference on DOM element for external access
    element._customSelect = this;

    this.init();
  }

  /**
   * Initialize event listeners and state
   */
  init() {
    // Get initial selected values from DOM
    this.options.forEach((option) => {
      if (option.classList.contains("is-selected")) {
        this.selectedValues.add(option.dataset.value);
      }
    });

    // Update clear button state
    this.updateClearButton();

    // Trigger click - toggle dropdown
    this.trigger.addEventListener("click", () => this.toggle());

    // Overlay click - close dropdown
    if (this.overlay) {
      this.overlay.addEventListener("click", () => this.close());
    }

    // Close button click (mobile)
    if (this.closeBtn) {
      this.closeBtn.addEventListener("click", () => this.close());
    }

    // Option clicks - toggle selection
    this.options.forEach((option) => {
      option.addEventListener("click", () => this.toggleOption(option));
    });

    // Clear / Select-all button
    if (this.clearBtn) {
      this.clearBtn.addEventListener("click", () => this.toggleSelectAll());
    }

    // Reposition floating elements on resize while open
    this._resizeHandler = () => {
      if (this.isOpen && this.drawer) {
        this.positionFloatingElements();
      }
    };
    window.addEventListener("resize", this._resizeHandler);

    // Keyboard navigation
    this.root.addEventListener("keydown", (e) => this.handleKeydown(e));
    if (this.dropdown) {
      this.dropdown.addEventListener("keydown", (e) => this.handleKeydown(e));
    }

    // Outside-click detection has to treat the portaled dropdown and overlay as
    // part of the same control, otherwise opening inside the drawer would close immediately.
    document.addEventListener("click", (e) => {
      if (this.isOpen && !this.isEventInsideSelect(e.target)) {
        this.close();
      }
    });

    // Close on escape — stopImmediatePropagation prevents other document-level
    // keydown listeners (e.g. drawer close) from also firing on the same event.
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && this.isOpen) {
        e.stopImmediatePropagation();
        this.close();
        this.trigger.focus();
      }
    });
  }

  /**
   * Toggle dropdown open/closed
   */
  toggle() {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  /**
   * Open the dropdown
   */
  open() {
    this.isOpen = true;
    this.root.classList.add("is-open");
    this.trigger.setAttribute("aria-expanded", "true");

    if (this.drawer) {
      this.portalFloatingElement(this.overlay, "overlay");
      this.portalFloatingElement(this.dropdown, "dropdown");
      this.positionFloatingElements();
    }

    if (this.overlay) {
      this.overlay.classList.add("opacity-40", "visible");
      this.overlay.classList.remove("invisible");
    }

    if (this.dropdown) {
      this.dropdown.classList.add("opacity-100", "visible", "translate-y-0");
      this.dropdown.classList.remove(
        "opacity-0",
        "invisible",
        "-translate-y-8",
      );
    }

    // Prevent body scroll on mobile when drawer is open
    // Only set if not already hidden (e.g., by scrolling cards drawer)
    if (window.innerWidth < 768 && document.body.style.overflow !== "hidden") {
      this.didSetBodyOverflow = true;
      document.body.style.overflow = "hidden";
    } else {
      this.didSetBodyOverflow = false;
    }

    // Focus first option
    const firstOption = this.dropdown.querySelector(
      ".js-select-option:not([disabled])",
    );
    if (firstOption) {
      firstOption.focus();
    }
  }

  /**
   * Close the dropdown
   */
  close() {
    this.isOpen = false;
    this.root.classList.remove("is-open");
    this.trigger.setAttribute("aria-expanded", "false");

    if (this.overlay) {
      this.overlay.classList.remove("opacity-40", "visible");
      this.overlay.classList.add("invisible");
    }

    if (this.dropdown) {
      this.dropdown.classList.remove("opacity-100", "visible", "translate-y-0");
      this.dropdown.classList.add("opacity-0", "invisible");
    }

    if (this.drawer) {
      this.resetFloatingElementStyles(this.overlay);
      this.resetFloatingElementStyles(this.dropdown);
      this.restoreFloatingElement(this.overlay, "overlay");
      this.restoreFloatingElement(this.dropdown, "dropdown");
    }

    // Restore body scroll only if we set it
    if (this.didSetBodyOverflow) {
      document.body.style.overflow = "";
      this.didSetBodyOverflow = false;
    }
  }

  /**
   * Toggle option selection
   * @param {HTMLElement} option - The option element to toggle
   */
  toggleOption(option) {
    if (option.hasAttribute("disabled")) return;

    const value = option.dataset.value;
    const isSelected = this.selectedValues.has(value);

    if (isSelected) {
      this.selectedValues.delete(value);
      option.classList.remove("is-selected");
      option.setAttribute("aria-selected", "false");
    } else {
      this.selectedValues.add(value);
      option.classList.add("is-selected");
      option.setAttribute("aria-selected", "true");
    }

    this.updateBadge();
    this.updateClearButton();
    this.dispatchChangeEvent();
  }

  /**
   * Toggle select all / clear all
   */
  toggleSelectAll() {
    if (this.selectedValues.size > 0) {
      this.clearSelection();
    } else {
      this.selectAll();
    }
  }

  /**
   * Select all options
   */
  selectAll() {
    this.options.forEach((option) => {
      if (option.hasAttribute("disabled")) return;
      const value = option.dataset.value;
      this.selectedValues.add(value);
      option.classList.add("is-selected");
      option.setAttribute("aria-selected", "true");
    });

    this.updateBadge();
    this.updateClearButton();
    this.dispatchChangeEvent();
  }

  /**
   * Clear all selections
   */
  clearSelection() {
    this.selectedValues.clear();

    this.options.forEach((option) => {
      option.classList.remove("is-selected");
      option.setAttribute("aria-selected", "false");
    });

    this.updateBadge();
    this.updateClearButton();
    this.dispatchChangeEvent();
  }

  /**
   * Update the badge with selected count
   */
  updateBadge() {
    if (!this.badge) return;

    const count = this.selectedValues.size;
    this.badge.textContent = count > 0 ? count : "";
    this.badge.setAttribute(
      "aria-label",
      count > 0 ? `${count} selected` : "selected count",
    );
  }

  /**
   * Update clear button label and disabled state.
   * When nothing is selected the button reads "Vali kõik" (select all);
   * when at least one option is selected it reads "Tühista valikud" (clear).
   */
  updateClearButton() {
    if (!this.clearBtn) return;

    const hasSelection = this.selectedValues.size > 0;
    const label = this.clearBtn.querySelector("span");
    if (label) {
      label.textContent = hasSelection ? "Tühista valikud" : "Vali kõik";
    }
    this.clearBtn.setAttribute(
      "aria-label",
      hasSelection ? "Tühista valikud" : "Vali kõik",
    );

    // Show the cross icon only when "Tühista valikud" is active
    const icon = this.clearBtn.querySelector("svg");
    if (icon) {
      icon.classList.toggle("hidden", !hasSelection);
    }
  }

  /**
   * Dispatch custom change event
   */
  dispatchChangeEvent() {
    const event = new CustomEvent("custom-select:change", {
      detail: {
        values: Array.from(this.selectedValues),
      },
      bubbles: true,
    });
    this.root.dispatchEvent(event);
  }

  /**
   * Handle keyboard navigation
   * @param {KeyboardEvent} e
   */
  handleKeydown(e) {
    if (!this.isOpen) {
      // Open on arrow down/up or space/enter when trigger is focused
      if (
        e.target === this.trigger &&
        (e.key === "ArrowDown" ||
          e.key === "ArrowUp" ||
          e.key === " " ||
          e.key === "Enter")
      ) {
        e.preventDefault();
        this.open();
      }
      return;
    }

    const focusedOption = document.activeElement;
    const optionsArray = Array.from(this.options).filter(
      (opt) => !opt.hasAttribute("disabled"),
    );
    const currentIndex = optionsArray.indexOf(focusedOption);

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        if (currentIndex < optionsArray.length - 1) {
          optionsArray[currentIndex + 1].focus();
        }
        break;

      case "ArrowUp":
        e.preventDefault();
        if (currentIndex > 0) {
          optionsArray[currentIndex - 1].focus();
        }
        break;

      case " ":
      case "Enter":
        e.preventDefault();
        if (focusedOption.classList.contains("js-select-option")) {
          this.toggleOption(focusedOption);
        }
        break;

      case "Home":
        e.preventDefault();
        optionsArray[0]?.focus();
        break;

      case "End":
        e.preventDefault();
        optionsArray[optionsArray.length - 1]?.focus();
        break;

      case "Tab": {
        // Keep focus inside the temporary dropdown layer instead of sending Tab
        // into the drawer behind it while the select is open.
        e.preventDefault();

        if (e.shiftKey) {
          // Shift+Tab: previous option, or close if at first
          if (currentIndex <= 0) {
            this.close();
            this.trigger.focus();
          } else {
            optionsArray[currentIndex - 1].focus();
          }
        } else {
          // Tab: next option, or wrap to first if at last
          if (currentIndex >= optionsArray.length - 1) {
            optionsArray[0].focus();
          } else {
            optionsArray[currentIndex + 1].focus();
          }
        }
        break;
      }
    }
  }

  /**
   * Check whether an event target belongs to this select, including any
   * portaled floating elements.
   * @param {EventTarget} target
   * @returns {boolean}
   */
  isEventInsideSelect(target) {
    return (
      this.root.contains(target) ||
      this.dropdown?.contains(target) ||
      this.overlay?.contains(target)
    );
  }

  /**
   * Move a floating element to <body> so transformed ancestors do not affect it.
   * @param {HTMLElement|null} element
   * @param {string} key
   */
  portalFloatingElement(element, key) {
    if (!element || element.parentNode === document.body) return;

    this[`${key}OriginalParent`] = element.parentNode;
    this[`${key}OriginalNextSibling`] = element.nextSibling;
    document.body.appendChild(element);
  }

  /**
   * Restore a previously portaled floating element to its original position.
   * @param {HTMLElement|null} element
   * @param {string} key
   */
  restoreFloatingElement(element, key) {
    const originalParent = this[`${key}OriginalParent`];
    const originalNextSibling = this[`${key}OriginalNextSibling`];

    if (!element || !originalParent) return;

    if (
      originalNextSibling &&
      originalNextSibling.parentNode === originalParent
    ) {
      originalParent.insertBefore(element, originalNextSibling);
    } else {
      originalParent.appendChild(element);
    }

    this[`${key}OriginalParent`] = null;
    this[`${key}OriginalNextSibling`] = null;
  }

  /**
   * Position the portaled overlay and dropdown inside the drawer bounds.
   */
  positionFloatingElements() {
    if (!this.drawer) return;

    const drawerRect = this.drawer.getBoundingClientRect();

    if (this.overlay) {
      this.overlay.style.transitionProperty = "opacity, visibility";
      this.overlay.style.top = `${drawerRect.top}px`;
      this.overlay.style.left = `${drawerRect.left}px`;
      this.overlay.style.right = "auto";
      this.overlay.style.bottom = "auto";
      this.overlay.style.width = `${drawerRect.width}px`;
      this.overlay.style.height = `${drawerRect.height}px`;
    }

    if (!this.dropdown) return;

    this.dropdown.style.position = "fixed";
    this.dropdown.style.zIndex = "100";
    this.dropdown.style.right = "auto";

    if (window.innerWidth < 768) {
      // Mobile uses the dropdown as a drawer sheet anchored to the current drawer,
      // not to the select trigger, so it can cover the full available width.
      this.dropdown.style.left = `${drawerRect.left}px`;
      this.dropdown.style.bottom = "0px";
      this.dropdown.style.top = "auto";
      this.dropdown.style.width = `${drawerRect.width}px`;
      return;
    }

    const triggerRect = this.trigger.getBoundingClientRect();
    const dropdownWidth = this.dropdown.offsetWidth || 280;
    const desiredLeft = triggerRect.right - dropdownWidth;
    const maxLeft = drawerRect.right - dropdownWidth;
    // Clamp desktop positioning to the drawer bounds because the trigger can sit
    // inside transformed content and would otherwise let the dropdown bleed outside.
    const left = Math.max(drawerRect.left, Math.min(desiredLeft, maxLeft));

    this.dropdown.style.top = `${triggerRect.bottom + 16}px`;
    this.dropdown.style.left = `${left}px`;
    this.dropdown.style.bottom = "auto";
  }

  /**
   * Clear inline positioning styles applied while floating elements are portaled.
   * @param {HTMLElement|null} element
   */
  resetFloatingElementStyles(element) {
    if (!element) return;

    element.style.top = "";
    element.style.left = "";
    element.style.right = "";
    element.style.bottom = "";
    element.style.width = "";
    element.style.height = "";
    element.style.position = "";
    element.style.zIndex = "";
    element.style.transitionProperty = "";
  }

  /**
   * Refresh the cached options NodeList after the DOM has been updated
   * (e.g. when options are populated dynamically).
   * Re-binds click handlers on new options.
   */
  refreshOptions() {
    this.options = this.root.querySelectorAll(".js-select-option");

    this.options.forEach((option) => {
      option.addEventListener("click", () => this.toggleOption(option));
    });
  }

  /**
   * Get currently selected values
   * @returns {string[]}
   */
  getSelectedValues() {
    return Array.from(this.selectedValues);
  }

  /**
   * Set selected values programmatically
   * @param {string[]} values - Array of values to select
   */
  setSelectedValues(values) {
    this.selectedValues.clear();

    this.options.forEach((option) => {
      const value = option.dataset.value;
      if (values.includes(value)) {
        this.selectedValues.add(value);
        option.classList.add("is-selected");
        option.setAttribute("aria-selected", "true");
      } else {
        option.classList.remove("is-selected");
        option.setAttribute("aria-selected", "false");
      }
    });

    this.updateBadge();
    this.updateClearButton();
  }
}

/**
 * Initialize all custom select components on the page
 */
function initCustomSelect() {
  const selects = document.querySelectorAll("[data-custom-select]");
  const instances = [];

  selects.forEach((select) => {
    instances.push(new CustomSelect(select));
  });

  return instances;
}

export default initCustomSelect;
export { CustomSelect };
